import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import ItemsGrid from "../../../Shared/ItemsGrid/ItemsGrid";
import { setFavoriteRest, getHomeProducts, getShowMoreProducts, getPriceSortedShowMoreProducts } from "../../../../services/items/actions";
import { 
  addPhase2Product, 
  removePhase2Product, 
  updatePhase2CartProduct 
} from "../../../../services/cart/actions";
import { updateCart } from "../../../../services/total/actions";
import "./ItemsSection.css";

const ItemsSection = ({ 
  tag, 
  showSearchOnly, 
  phase2CartProducts,
  user, 
  setFavoriteRest,
  addPhase2Product,
  removePhase2Product,
  updatePhase2CartProduct,
  handleAddOnOpen,
  checkRecommended,
  storeColor = '#22c55e',
  index,
  categoryLength,
  searching = false,
  recommendedItems,
  isItemRecommended,
  recommendedItemIndex,
  recommendedItemCategory,
  homeProducts,
  updateCart,
  getShowMoreProducts,
  getPriceSortedShowMoreProducts,
  currentSortFilter = 'distance', // New prop for current sort filter
  currentSortOrder = 'asc', // New prop for current sort order
  // New props for ProductsPage functionality
  customProducts = null, // Custom products array (for subcategory products)
  showAllItems = false, // Show all items at once without pagination
  hideShowMore = false, // Hide the Show More button
  hideTitle = false, // Hide the section title
  ...props 
}) => {
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]); // Store all items from API (up to 18)
  const [displayedItems, setDisplayedItems] = useState([]); // Items currently displayed
  const [loading, setLoading] = useState(false);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [update, setUpdate] = useState(true);
  const [currentPage, setCurrentPage] = useState(1); // Pagination state
  const [hasMoreItems, setHasMoreItems] = useState(false); // Whether more items are available
  
  const ITEMS_PER_PAGE = 6;
  const MAX_TOTAL_ITEMS = 24; // Maximum items to display per section (6 initial + 18 from show more)

  const forceStateUpdate = () => {
    setTimeout(() => {
      setUpdate(!update);
      updatePhase2CartProduct();
    }, 100);
  };

  // Store a ref to track if we've initialized this component with current data
  const initializedRef = React.useRef(null);
  
  useEffect(() => {
    // Handle custom products (for ProductsPage)
    if (customProducts && Array.isArray(customProducts)) {
      console.log(`🛍️ Using custom products for ${tag}:`, customProducts.length, 'items');
      
      // Transform custom products to match expected format
      const transformedCustomProducts = customProducts.map((item, index) => {
        // Ensure localStorage setting for percentage discount is enabled
        if (!localStorage.getItem("showPercentageDiscount")) {
          localStorage.setItem("showPercentageDiscount", "true");
        }
        
        // Debug logging for ribbon display
        console.log(`🎗️ Product ${item.name}:`, {
          price: item.price,
          old_price: item.old_price,
          hasOldPrice: item.old_price && parseFloat(item.old_price) > 0,
          showPercentageDiscount: localStorage.getItem("showPercentageDiscount")
        });
        
        // Debug logging for cart validation
        console.log(`🛒 Custom Product Cart Validation - ${item.name}:`, {
          id: item.id,
          restaurant_id: item.restaurant_id, // Using real restaurant_id from API
          name: item.name,
          price: item.price,
          item_category_id: item.item_category_id,
          hasAllRequiredFields: !!(item.id && item.restaurant_id && item.name && item.price)
        });
        
        return {
          id: item.id,
          name: item.name,
          price: typeof item.price === 'string' ? item.price : item.price.toString(),
          old_price: item.old_price && parseFloat(item.old_price) > 0 ? 
            (typeof item.old_price === 'string' ? item.old_price : item.old_price.toString()) : "0",
          image: item.image || item.placeholder,
          is_active: item.is_active !== undefined ? item.is_active : 1,
          stock_status: item.stock_status || "available",
          stock: item.stock || 10,
          addon_categories: item.addon_categories || [],
          quantity: item.quantity || 1,
          is_favorited: item.is_favorited || 0,
          brand: item.brand || "",
          is_veg: item.is_veg || 0,
          description: item.description || null,
          restaurant: item.restaurant || null,
          restaurant_id: item.restaurant_id, // Using real restaurant_id from API
          item_category: item.item_category || null,
          item_category_id: item.item_category_id || null, // Required for cart validation
          master_item: item.master_item || null,
          selectedaddons: [],
          zone_id: localStorage.getItem('zone_id') || null,
          cart_type: 'phase2',
          // Badge properties for ItemBadge component
          is_top_picks: item.is_top_picks || 0,
          is_popular: item.is_popular || 0,
          is_new: item.is_new || 0,
          rating: item.rating || null
        };
      });
      
      if (showAllItems) {
        // Show all items at once without pagination
        setItems(transformedCustomProducts);
        setDisplayedItems(transformedCustomProducts);
        setAllItems(transformedCustomProducts);
        setHasMoreItems(false); // No more items to show
      } else {
        // Use normal pagination
        const initialItems = transformedCustomProducts.slice(0, 6);
        setItems(initialItems);
        setDisplayedItems(initialItems);
        setAllItems(transformedCustomProducts);
        setHasMoreItems(transformedCustomProducts.length > 6);
      }
      
      setCurrentPage(1);
      return;
    }
    
    // Original logic for homeProducts
    // Create a unique key for current data state to prevent unnecessary resets
    let dataKey = `${tag}-${currentSortFilter}-no-data`;
    
    if (homeProducts && homeProducts.tagged_items) {
      const tagKey = tag === 'top_picks' ? 'is_top_picks' : tag === 'new' ? 'is_new' : 'is_popular';
      const tagItems = homeProducts.tagged_items[tagKey];
      
      if (tagItems && Array.isArray(tagItems)) {
        const first6Items = tagItems.slice(0, 6);
        const itemIds = first6Items.map(item => item && item.id ? item.id : 'invalid');
        dataKey = `${tag}-${currentSortFilter}-${JSON.stringify(itemIds)}`;
      }
    }
    
    // Only reset if we haven't initialized with this data yet, or if the data has actually changed
    if (initializedRef.current === dataKey) {
      // console.log(`🔥 Skipping useEffect for ${tag} - already initialized with this data`);
      return;
    }
    
    // console.log(`🔥 ItemsSection useEffect triggered for ${tag} - NEW DATA DETECTED`);
    // console.log(`🔥 Previous key: ${initializedRef.current}`);
    // console.log(`🔥 New key: ${dataKey}`);
    
    if (homeProducts && homeProducts.tagged_items) {
      let apiItems = [];
      
      // Get EXACT items from API based on tag - Initial 6 items
      if (tag === 'top_picks') {
        apiItems = homeProducts.tagged_items.is_top_picks || [];
      } else if (tag === 'popular') {
        apiItems = homeProducts.tagged_items.is_popular || [];
      } else if (tag === 'new') {
        apiItems = homeProducts.tagged_items.is_new || [];
      }

      // Take only first 6 items for initial display
      const initialItems = apiItems.slice(0, 6);

      // Validate each item before transformation
      const validatedItems = initialItems.filter((item, index) => {
        const isValid = (
          item &&
          typeof item === 'object' &&
          typeof item.id === 'number' &&
          typeof item.name === 'string' &&
          typeof item.image === 'string' &&
          typeof item.price === 'number' &&
          item.name.trim() !== '' &&
          item.image.trim() !== ''
        );
        
        if (!isValid) {
          console.warn(`⚠️ Invalid item at index ${index} for ${tag}:`, item);
        }
        
        return isValid;
      });

      // Transform ONLY the validated items
      const transformedItems = validatedItems.map((item, index) => {
        return {
        id: item.id,
        name: item.name,
        price: item.price.toString(),
        old_price: item.old_price ? item.old_price.toString() : "0",
        image: item.image,
        is_active: 1,
        stock_status: "available",
        stock: 10,
        addon_categories: [],
        quantity: 1,
        is_favorited: 0,
        brand: item.master_item ? item.master_item.manufacturer_name : "",
          is_veg: item.is_veg || 0,
          description: item.description || null,
          restaurant: item.restaurant || null,
        restaurant_id: item.restaurant ? item.restaurant.id : null,
          item_category: item.item_category || null,
        item_category_id: item.item_category ? item.item_category.id : null,
          master_item: item.master_item || null,
        selectedaddons: [],
          zone_id: localStorage.getItem('zone_id') || null,
          cart_type: 'phase2'
        };
      });

      // console.log(`🔥 INITIALIZING STATE for ${tag} with ${transformedItems.length} items - currentSortFilter: ${currentSortFilter}`);
      
      // Set initial state
      setItems(transformedItems);
      setDisplayedItems(transformedItems);
      setAllItems(transformedItems); // Initially same as items
      setCurrentPage(1);
      
      // Always show "Show More" button after initial 6 items from homeProducts
      setHasMoreItems(transformedItems.length === 6);
      
      // Mark this data as initialized
      initializedRef.current = dataKey;
      
    } else {
      // console.log(`🔥 No homeProducts or tagged_items found for ${tag}`);
      setItems([]);
      setDisplayedItems([]);
      setAllItems([]);
      setHasMoreItems(false);
      initializedRef.current = dataKey;
    }
  }, [customProducts, showAllItems, homeProducts, tag, currentSortFilter]);

  // Handle Show More button click
  const handleShowMore = async () => {
    // console.log(`🔥 Show More clicked for ${tag}, current page: ${currentPage}`);
    // console.log(`🔥 Current sort filter: ${currentSortFilter}, sort order: ${currentSortOrder}`);
    
    setShowMoreLoading(true);
    
    try {
      // Get user location from localStorage
      const userSetAddress = JSON.parse(localStorage.getItem("userSetAddress"));
      if (!userSetAddress) {
        console.error('🔥 No user address found in localStorage');
        return;
      }

      // Map tag to API tag parameter
      const getApiTag = (tag) => {
        switch(tag) {
          case 'top_picks': return 'is_top_picks';
          case 'popular': return 'is_popular';
          case 'new': return 'is_new';
          default: return 'is_top_picks';
        }
      };

      const apiTag = getApiTag(tag);
      const nextPage = currentPage + 1;
      
      // console.log(`🔥 Loading page ${nextPage} for ${tag} (API tag: ${apiTag})`);
      
      let apiResponse;
      
      // Check if price sorting is active
      const isPriceSortActive = (
        currentSortFilter === 'price-high-to-low' || 
        currentSortFilter === 'price-low-to-high'
      );
      
      // console.log(`🔥 isPriceSortActive: ${isPriceSortActive}, currentSortFilter: ${currentSortFilter}`);
      
      if (isPriceSortActive) {
        // Use price sorted show more API
        const sortOrder = currentSortFilter === 'price-high-to-low' ? 'desc' : 'asc';
        // console.log(`💰🔥 Using PRICE SORTED Show More API with sort order: ${sortOrder}`);
        
        apiResponse = await getPriceSortedShowMoreProducts(
          userSetAddress.lat,
          userSetAddress.lng,
          userSetAddress.state || 'Karnataka',
          apiTag,
          nextPage,
          sortOrder,
          ITEMS_PER_PAGE
        );
      } else {
        // Use regular distance-based show more API
        // console.log(`🎯🔥 Using DISTANCE-BASED Show More API`);
        
        apiResponse = await getShowMoreProducts(
          userSetAddress.lat,
          userSetAddress.lng,
          userSetAddress.state || 'Karnataka',
          apiTag,
          nextPage,
          ITEMS_PER_PAGE
        );
      }
      
      // console.log(`🔥 Show More Action Response for ${tag}:`, apiResponse);
      // console.log(`🔥 API Response success:`, apiResponse.success);
      // console.log(`🔥 API Response items length:`, apiResponse.items ? apiResponse.items.length : 'NO ITEMS');
      
      if (!apiResponse.success || apiResponse.items.length === 0) {
        // console.log(`🔥 No new items received from API for ${tag}`);
        // Hide show more button if no items received or API failed
        setHasMoreItems(false);
        return;
      }
      
      // Transform API items to match component format
      // console.log(`🔥 About to transform ${apiResponse.items.length} new items for ${tag}`);
      const transformedNewItems = apiResponse.items.map((item, index) => {
        // console.log(`🔥 Transforming new API item ${index + 1} for ${tag}:`, item);
        return {
          id: item.id,
          name: item.name,
          price: item.price.toString(),
          old_price: item.old_price ? item.old_price.toString() : "0",
          image: item.image,
          is_active: 1,
          stock_status: "available",
          stock: 10,
          addon_categories: [],
          quantity: 1,
          is_favorited: 0,
          brand: item.master_item ? item.master_item.manufacturer_name : "",
          is_veg: item.is_veg || 0,
          description: item.description || null,
          restaurant: item.restaurant || null,
          restaurant_id: item.restaurant ? item.restaurant.id : null,
          item_category: item.item_category || null,
          item_category_id: item.item_category ? item.item_category.id : null,
          master_item: item.master_item || null,
          selectedaddons: [],
          zone_id: localStorage.getItem('zone_id') || null,
          cart_type: 'phase2'
        };
      });
      
      // console.log(`🔥 Transformed new items for ${tag}:`, transformedNewItems);
      // console.log(`🔥 Transformed new items count: ${transformedNewItems.length}`);
      
      // Add new items to allItems and displayedItems
      // console.log(`🔥 Current allItems length: ${allItems.length}`);
      // console.log(`🔥 Current displayedItems length: ${displayedItems.length}`);
      
      const updatedAllItems = [...allItems, ...transformedNewItems];
      const updatedDisplayedItems = [...displayedItems, ...transformedNewItems];
      
      // console.log(`🔥 Updated allItems length: ${updatedAllItems.length}`);
      // console.log(`🔥 Updated displayedItems length: ${updatedDisplayedItems.length}`);
      
      setAllItems(updatedAllItems);
      setDisplayedItems(updatedDisplayedItems);
      setCurrentPage(nextPage);
      
      // Check if we've reached the maximum total items (24) - Remove blocking logic
      const totalItemsLoaded = updatedAllItems.length;
      const hasReachedMax = totalItemsLoaded >= MAX_TOTAL_ITEMS; // Allow up to 24 items total
      const noMoreItems = transformedNewItems.length < ITEMS_PER_PAGE; // API returned less than 6 items
      
      // Continue showing "Show More" until we reach 24 items OR API has no more items
      setHasMoreItems(!hasReachedMax && !noMoreItems);
      
      // console.log(`🔥 Show More completed for ${tag}:`);
      // console.log(`🔥 Total items loaded: ${totalItemsLoaded} (6 from homeProducts + ${totalItemsLoaded - 6} from show more API)`);
      // console.log(`🔥 Current page: ${nextPage}`);
      // console.log(`🔥 Has more items: ${!hasReachedMax && !noMoreItems}`);
      
    } catch (error) {
      console.error(`🔥 Error loading more items for ${tag}:`, error);
      // Don't hide the button on error, user can try again
    } finally {
      setShowMoreLoading(false);
    }
  };

  // Map tag to proper section title
  const getTagTitle = (tag) => {
    switch(tag) {
      case 'top_picks': return 'Top Picks';
      case 'new': return 'New Arrivals'; 
      case 'popular': return 'Most Popular';
      case 'search': return 'Search';
      default: return 'Products';
    }
  };

  // Restaurant object for ItemGrid component
  const restaurant = {
    isMapped: 1,
    id: 1,
    slug: "phase2-store",
    store_recommendation: 1,
    certificate: false
  };

  // If this is search only, render the search bar
  if (showSearchOnly) {
    return (
      <div className="sticky-search-bar-wrapper">
        <div className="sticky-search-bar">
          <Link
            to="/phase2/explore"
            className="anchor-link"
            style={{ width: "100%", display: "block" }}
          >
            <div className="mock-search-block">
              <div className="search-inner">
                <div>
                  <span style={{ color: "#777" }}>
                    {localStorage.getItem("mockSearchPlaceholder") || "Search..."}
                  </span>
                </div>
                <div>
                  <i className="si si-magnifier" style={{ color: "#555" }} />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    );
  }

  const sectionTitle = getTagTitle(tag);

  const toggleSection = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="phase2-items-section">
      <div className="section-container">
        {/* Conditionally render section header based on hideTitle prop */}
        {!hideTitle && (
          <div className="section-header" onClick={toggleSection}>
            <div className="section-title">
              {sectionTitle}
            </div>
            <div className={`section-arrow ${isOpen ? 'open' : ''}`}>
              <i className="si si-arrow-down" />
            </div>
          </div>
        )}
        <div className={`section-content ${(isOpen || hideTitle) ? 'open' : ''}`}>
          {loading ? (
            <div className="loading-spinner">Loading...</div>
          ) : displayedItems.length > 0 ? (
            <>
            <ItemsGrid
                items={displayedItems}
                category={null}
              restaurant={restaurant}
                cartProducts={phase2CartProducts}
              user={user}
                addProduct={addPhase2Product}
                removeProduct={removePhase2Product}
              forceStateUpdate={forceStateUpdate}
              handleAddOnOpen={handleAddOnOpen}
                checkRecommended={() => {}}
              storeColor={storeColor}
              index={index}
              categoryLength={categoryLength}
              searching={searching}
                setFavoriteRest={setFavoriteRest}
                recommendedItems={[]}
                isItemRecommended={false}
                recommendedItemIndex={null}
                recommendedItemCategory={null}
                selectedItemId={null}
              />
              
              {/* Conditionally render Show More Button based on hideShowMore prop */}
              {hasMoreItems && !hideShowMore && (
                <div className="show-more-container" style={{ 
                  textAlign: 'center', 
                  marginTop: '15px',
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  
                }}>
                  <button
                    className="show-more-btn"
                    onClick={handleShowMore}
                    disabled={showMoreLoading}
                                          style={{
                        backgroundColor: showMoreLoading ? '#e0e0e0' : '#f8f9fa',
                        color: showMoreLoading ? '#999' : '#495057',
                        border: '1px solid #dee2e6',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: showMoreLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        minWidth: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (!showMoreLoading) {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.borderColor = '#adb5bd';
                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)';
                          e.target.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showMoreLoading) {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.borderColor = '#dee2e6';
                          e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
                          e.target.style.transform = 'translateY(0)';
                        }
                      }}
                  >
                                        {showMoreLoading ? (
                        <>
                          <span style={{ 
                            width: '16px', 
                            height: '16px',
                            border: '2px solid #dee2e6',
                            borderTop: '2px solid #495057',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <span>Show More</span>
                          <i className="si si-arrow-down" style={{ 
                            fontSize: '12px',
                            transition: 'transform 0.3s ease'
                          }}></i>
                        </>
                      )}
                  </button>
                  
                                     {/* Progress indicator */}
                   {/* <div style={{
                     marginTop: '8px',
                     fontSize: '12px',
                     color: '#666',
                     opacity: 0.7
                   }}>
                     Showing {displayedItems.length} of {MAX_TOTAL_ITEMS} items
                   </div> */}
                </div>
              )}
            </>
          ) : (
            <div className="no-items">No items found</div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  phase2CartProducts: state.cart && state.cart.phase2Products ? state.cart.phase2Products : [],
  user: state.user && state.user.user ? state.user.user : null,
  recommendedItems: state.items && state.items.recommended_items ? state.items.recommended_items : [],
  isItemRecommended: state.items && state.items.is_item_recommended,
  recommendedItemIndex: state.items && state.items.recommended_item_index,
  recommendedItemCategory: state.items && state.items.recommended_item_category
});

export default connect(mapStateToProps, {
  setFavoriteRest,
  addPhase2Product,
  removePhase2Product,
  updatePhase2CartProduct,
  getHomeProducts,
  getShowMoreProducts,
  getPriceSortedShowMoreProducts,
  updateCart,
  handleAddOnOpen: (item) => ({ type: 'OPEN_ADDON_MODAL', payload: item }),
  checkRecommended: (item, index, category, categoryLength) => {
    // Completely disabled to prevent extra items
    // console.log('🔥 checkRecommended DISABLED to prevent extra items');
  }
})(ItemsSection);
